'use client'

import { type FormEvent, useId, useState } from 'react'
import Link from 'next/link'
import { AlertCircle, CheckCircle } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/tls/spinner'
import { withRequestProofHeaders } from '@/lib/request-proof'
import { cn } from '@/lib/utils'

type PublishSource = 'convert' | 'voice'

type PublishCollection = {
  slug: string
  name: string
  href: string
}

type PublishGateResponse = {
  kind: 'allow' | 'hold' | 'block'
  message: string
  collection: PublishCollection | null
}

type PublishToLibraryProps = {
  defaultTitle?: string
  defaultDescription?: string
  filename: string
  durationSeconds: number | null
  source: PublishSource
  ready: boolean
}

type PublishUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'result'; payload: PublishGateResponse }
  | { kind: 'error'; message: string }

function isPublishGateResponse(value: unknown): value is PublishGateResponse {
  if (typeof value !== 'object' || value === null || !('kind' in value) || !('message' in value)) {
    return false
  }
  if (value.kind !== 'allow' && value.kind !== 'hold' && value.kind !== 'block') {
    return false
  }
  if (typeof value.message !== 'string') return false
  return true
}

export function PublishToLibrary({
  defaultTitle = '',
  defaultDescription = '',
  filename,
  durationSeconds,
  source,
  ready,
}: PublishToLibraryProps) {
  const titleId = useId()
  const descriptionId = useId()
  const websiteId = useId()
  const [title, setTitle] = useState(defaultTitle)
  const [description, setDescription] = useState(defaultDescription)
  const [website, setWebsite] = useState('')
  const [state, setState] = useState<PublishUiState>({ kind: 'idle' })

  if (!ready) return null

  const submitting = state.kind === 'submitting'
  const blocked = state.kind === 'result' && state.payload.kind === 'block'

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) return

    const trimmedTitle = title.trim()
    if (trimmedTitle.length < 2) {
      setState({
        kind: 'error',
        message: 'Add a title of at least 2 characters before listing.',
      })
      return
    }

    setState({ kind: 'submitting' })

    try {
      const headers = await withRequestProofHeaders({
        'Content-Type': 'application/json',
      })
      const response = await fetch('/api/create/publish', {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: JSON.stringify({
          title: trimmedTitle,
          description: description.trim(),
          filename,
          durationSeconds,
          source,
          website,
        }),
      })
      const payload: unknown = await response.json().catch(() => null)

      if (!response.ok) {
        const message =
          typeof payload === 'object' &&
          payload !== null &&
          'error' in payload &&
          typeof payload.error === 'string'
            ? payload.error
            : 'The listing check did not finish. Download still works. Try submit again in a minute.'
        setState({ kind: 'error', message })
        return
      }

      if (!isPublishGateResponse(payload)) {
        setState({
          kind: 'error',
          message: 'The listing check returned an unexpected result. Download still works.',
        })
        return
      }

      setState({ kind: 'result', payload })
    } catch {
      setState({
        kind: 'error',
        message: 'The listing check could not reach the server. Check your connection, then try again. Download still works.',
      })
    }
  }

  return (
    <section className="space-y-4 border-t border-[color:var(--tls-border-subtle)] pt-5">
      <div>
        <h2 className="tls-eyebrow mb-2">Public library</h2>
        <p className="text-sm text-[var(--tls-fg-muted)]">
          Optional. Send the title and description for a listing check before anything goes public. The WAV stays in this browser until you download it.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor={titleId} className="text-sm font-medium text-[var(--tls-fg-strong)]">
            Title
          </label>
          <Input
            id={titleId}
            name="title"
            type="text"
            maxLength={100}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoComplete="off"
            required
            aria-invalid={state.kind === 'error' && title.trim().length < 2}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor={descriptionId} className="text-sm font-medium text-[var(--tls-fg-strong)]">
            Description <span className="font-normal text-[var(--tls-fg-subtle)]">(optional)</span>
          </label>
          <Input
            id={descriptionId}
            name="description"
            type="text"
            maxLength={280}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="sr-only" aria-hidden="true">
          <label htmlFor={websiteId}>Website</label>
          <input
            id={websiteId}
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || blocked}
          className={cn(
            'tlsr-pill min-h-[48px] w-full justify-center',
            submitting && 'cursor-wait',
            (submitting || blocked) && 'cursor-not-allowed opacity-40',
            !submitting && !blocked && 'is-active'
          )}
        >
          {submitting ? (
            <>
              <Spinner size={14} color="currentColor" />
              <span>Checking listing…</span>
            </>
          ) : blocked ? (
            <span>Listing blocked</span>
          ) : (
            <span>Submit to the public library</span>
          )}
        </button>
      </form>

      {state.kind === 'error' && (
        <div
          className="flex items-start gap-2 border-t border-[color:var(--tls-border-subtle)] pt-3 text-sm text-[var(--tls-fg-strong)]"
          role="alert"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p className="font-medium">{state.message}</p>
        </div>
      )}

      {state.kind === 'result' && (
        <div
          className="flex items-start gap-2 border-t border-[color:var(--tls-border-subtle)] pt-3 text-sm text-[var(--tls-fg-strong)]"
          role={state.payload.kind === 'allow' ? 'status' : 'alert'}
        >
          {state.payload.kind === 'allow' ? (
            <CheckCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          ) : (
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          )}
          <div className="space-y-1">
            <p className="font-medium">{state.payload.message}</p>
            {state.payload.collection && state.payload.kind !== 'block' && (
              <p className="text-[var(--tls-fg-muted)]">
                Collection:{' '}
                <Link
                  href={state.payload.collection.href}
                  className="text-[var(--tls-brand-ink)] underline-offset-4 hover:underline"
                >
                  {state.payload.collection.name}
                </Link>
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
